// SCME v1.0 · Fase 4 · Diálogo de análisis de un SCME_EXPORT.zip entrante
// ---------------------------------------------------------------------------
// Flujo:
//   1. El usuario selecciona un ZIP (input file).
//   2. Se envía a POST /api/scme/import/analyze (requiere PIN).
//   3. Se muestra el informe visual:
//        - Manifest de origen
//        - Verdict global (adopt / review / reject)
//        - Resumen numérico
//        - Detalle por plugin (colisiones, deps faltantes, env faltantes)
//        - Errores de esquema
//
// Restricción total: NO se instala nada. NO se copia código. La adopción
// real siempre requiere una copia validada humana.
// ---------------------------------------------------------------------------
import React, { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Upload,
  Loader2,
  FileArchive,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Info,
  Package,
  Layers3,
} from "lucide-react";

const VERDICT_STYLES = {
  adopt:  { label: "Adoptable",         cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  review: { label: "Requiere revisión", cls: "bg-amber-100 text-amber-800 border-amber-200",       icon: AlertTriangle },
  reject: { label: "Rechazado",         cls: "bg-rose-100 text-rose-800 border-rose-200",           icon: XCircle },
};

function VerdictBadge({ verdict }) {
  const s = VERDICT_STYLES[verdict] || VERDICT_STYLES.review;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${s.cls}`}>
      <Icon className="h-3.5 w-3.5" />
      {s.label}
    </span>
  );
}

export default function PluginsImportDialog({ open, onOpenChange }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setReport(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handlePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setReport(null);
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const resp = await api.post("/scme/import/analyze", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(resp.data);
      toast.success("Informe generado");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error analizando el ZIP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="scme-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <Upload className="h-5 w-5 text-orange-600" />
            Importar SCME_EXPORT.zip
            <span className="text-[10px] font-mono rounded bg-neutral-100 text-neutral-700 px-1.5 py-0.5">
              Solo análisis
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Analiza el paquete y genera un informe. No instala nada, no copia código.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* Uploader */}
          <div className="rounded-lg border-2 border-dashed border-neutral-300 bg-neutral-50 p-4">
            <div className="flex items-center gap-3">
              <FileArchive className="h-6 w-6 text-neutral-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <input
                  ref={inputRef}
                  type="file"
                  accept=".zip,application/zip,application/x-zip-compressed"
                  onChange={handlePick}
                  data-testid="scme-import-file"
                  className="block w-full text-xs text-neutral-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-orange-100 file:text-orange-800 hover:file:bg-orange-200"
                />
                {file && (
                  <div className="mt-1 text-[11px] text-neutral-600 font-mono truncate">
                    {file.name} · {(file.size / 1024).toFixed(1)} KB
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleAnalyze}
                disabled={!file || loading}
                data-testid="scme-import-analyze"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-60"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Analizando…</>
                ) : (
                  "Analizar"
                )}
              </button>
            </div>
          </div>

          {/* Informe */}
          {report && (
            <div className="space-y-3" data-testid="scme-import-report">
              {/* Verdict global */}
              <div className="rounded-lg border border-neutral-200 bg-white p-3 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
                    Recomendación global
                  </div>
                  <div className="mt-1"><VerdictBadge verdict={report.verdict} /></div>
                </div>
                <div className="text-right text-[11px] text-neutral-500">
                  <div>Origen: <code className="font-mono">{report.sourceProject || "—"}</code></div>
                  <div>SCME v{report.scmeVersion || "—"}</div>
                  <div className="mt-0.5">Generado: {report.generatedAt || "—"}</div>
                </div>
              </div>

              {/* Resumen numérico */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                <Stat label="Total" value={report.summary?.totalIncoming ?? 0} tone="neutral" />
                <Stat label="Adoptables" value={report.summary?.adopt ?? 0} tone="emerald" />
                <Stat label="A revisar" value={report.summary?.review ?? 0} tone="amber" />
                <Stat label="Rechazados" value={report.summary?.reject ?? 0} tone="rose" />
                <Stat label="Colisiones" value={report.summary?.collisions ?? 0} tone="blue" />
                <Stat label="Errores schema" value={report.summary?.schemaErrors ?? 0} tone="rose" />
              </div>

              {/* Errores schema */}
              {report.schemaErrors?.length > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle className="h-4 w-4 text-rose-700" />
                    <h4 className="font-semibold text-sm text-rose-900">Errores de esquema</h4>
                  </div>
                  <ul className="text-[11px] font-mono text-rose-800 space-y-0.5">
                    {report.schemaErrors.slice(0, 20).map((e, i) => (
                      <li key={i}>· {e}</li>
                    ))}
                    {report.schemaErrors.length > 20 && (
                      <li className="text-rose-600">… y {report.schemaErrors.length - 20} más</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Detalle por plugin */}
              <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
                <div className="px-3 py-2 border-b border-neutral-200 bg-neutral-50 flex items-center gap-2">
                  <Package className="h-4 w-4 text-neutral-500" />
                  <h4 className="font-semibold text-sm">Análisis por plugin</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm table-fixed">
                    <colgroup>
                      <col />
                      <col style={{ width: "90px" }} />
                      <col style={{ width: "90px" }} />
                      <col style={{ width: "160px" }} />
                      <col style={{ width: "260px" }} />
                    </colgroup>
                    <thead className="bg-neutral-50 text-neutral-600">
                      <tr className="text-left text-[10px] uppercase tracking-wider">
                        <th className="py-3 px-4 font-bold">Plugin</th>
                        <th className="py-3 px-4 font-bold">Versión</th>
                        <th className="py-3 px-4 font-bold">Score</th>
                        <th className="py-3 px-4 font-bold">Veredicto</th>
                        <th className="py-3 px-4 font-bold">Incidencias</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report.perPlugin || []).map((p) => (
                        <tr key={p.pluginId} data-testid={`scme-import-plugin-${p.pluginId}`} className="border-t border-neutral-100 h-[56px]">
                          <td className="py-3 px-4 align-middle">
                            <div className="font-semibold text-neutral-900 truncate" title={p.name}>{p.name}</div>
                            <div className="font-mono text-[10px] text-neutral-500 truncate">{p.pluginId}</div>
                          </td>
                          <td className="py-3 px-4 font-mono text-[12px] text-neutral-700 align-middle">v{p.version}</td>
                          <td className="py-3 px-4 align-middle">
                            <span className={`inline-block font-mono text-[12px] font-bold ${p.compatibilityScore >= 75 ? "text-emerald-700" : p.compatibilityScore >= 40 ? "text-amber-700" : "text-rose-700"}`}>
                              {p.compatibilityScore}%
                            </span>
                          </td>
                          <td className="py-3 px-4 align-middle"><VerdictBadge verdict={p.verdict} /></td>
                          <td className="py-3 px-4 text-[11px] align-middle">
                            {p.conflict && (
                              <div className="text-blue-700 truncate">
                                <Layers3 className="inline h-3 w-3 mr-0.5" />
                                Colisión con v{p.conflict.localVersion || "?"} local
                                {!p.conflict.sameVersion && <span className="text-rose-700"> (versión distinta)</span>}
                              </div>
                            )}
                            {p.missing?.python?.length > 0 && (
                              <div className="text-rose-700 truncate">
                                Faltan {p.missing.python.length} paquetes Python:{" "}
                                <code className="font-mono">{p.missing.python.slice(0, 3).join(", ")}</code>
                                {p.missing.python.length > 3 && "…"}
                              </div>
                            )}
                            {p.missing?.env?.length > 0 && (
                              <div className="text-amber-700 truncate">
                                Faltan {p.missing.env.length} vars .env:{" "}
                                <code className="font-mono">{p.missing.env.slice(0, 3).join(", ")}</code>
                                {p.missing.env.length > 3 && "…"}
                              </div>
                            )}
                            {!p.conflict && !p.missing?.python?.length && !p.missing?.env?.length && (
                              <span className="text-neutral-500">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Extra entries */}
              {report.extraEntries?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="h-4 w-4 text-amber-700" />
                    <h4 className="font-semibold text-sm text-amber-900">Entradas no declaradas en el contrato</h4>
                  </div>
                  <ul className="text-[11px] font-mono text-amber-800">
                    {report.extraEntries.map((e) => (<li key={e}>· {e}</li>))}
                  </ul>
                </div>
              )}

              <div className="rounded-md bg-neutral-900 text-neutral-100 text-[11px] px-3 py-2 flex items-start gap-2">
                <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-neutral-400" />
                <span>
                  {report.notes || "Sólo análisis. Ninguna adopción automática. La reutilización siempre requiere copia validada humana."}
                </span>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => { reset(); onOpenChange(false); }}
              data-testid="scme-import-close"
              className="px-3 py-2 text-sm rounded-lg border border-neutral-300 hover:bg-neutral-50"
            >
              Cerrar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone = "neutral" }) {
  const map = {
    neutral: "bg-neutral-50 border-neutral-200 text-neutral-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber:   "bg-amber-50 border-amber-200 text-amber-800",
    rose:    "bg-rose-50 border-rose-200 text-rose-800",
    blue:    "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <div className={`rounded-lg border p-2 text-center ${map[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="text-lg font-mono font-bold tabular-nums">{value}</div>
    </div>
  );
}
